import React from 'react'
import axios from 'axios'
import {
    MdArrowForward, MdCheckCircle, MdClose, MdEmail, MdHistory,
    MdLocationOn, MdOutlineMerge, MdPeople, MdPhone, MdShield, MdWarningAmber,
} from 'react-icons/md'
import { baseUrl } from '../../assets/Animations'
import { getRequestHeaders } from '../../api'
import './returning-family-merge-modal.css'

const MERGE_CONFIRMATION = 'MERGE_RETURNING_FAMILY'
const normalize = (value) => String(value || '').trim()
const formatAddress = (address = {}) => [
    address?.addressLine1,
    address?.addressLine2,
    [address?.city, address?.state].filter(Boolean).join(', '),
    address?.zipcode,
].map(normalize).filter(Boolean).join(' ')
const formatDate = (value) => {
    if (!value) return 'Not recorded'
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime())
        ? String(value)
        : parsed.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}
const displayRate = (student = {}) => {
    const rate = Number(student?.lessonRate)
    return Number.isFinite(rate) && rate > 0 ? `$${rate}` : 'No rate recorded'
}

const ProfileSummary = ({family, label, survivor = false}) => (
    <section className={`returning-family-merge__profile ${survivor ? 'is-survivor' : ''}`}>
        <div className='returning-family-merge__profile-heading'>
            <div><span>{label}</span><h3>{family?.familyName || 'Unnamed family'}</h3></div>
            <span className='returning-family-merge__status'>{family?.familyStatus || 'No status'}</span>
        </div>
        {survivor && <div className='returning-family-merge__survivor-note'>
            <MdShield aria-hidden='true' />
            This profile, its current contact information, and its onboarding card stay active.
        </div>}
        <dl className='returning-family-merge__contact-list'>
            <div><dt><MdEmail aria-hidden='true' /></dt><dd>{family?.primaryEmail || 'No email'}</dd></div>
            <div><dt><MdPhone aria-hidden='true' /></dt><dd>{family?.primaryCellphone || family?.primaryHomePhone || 'No phone'}</dd></div>
            <div><dt><MdLocationOn aria-hidden='true' /></dt><dd>{formatAddress(family?.address) || 'No address'}</dd></div>
        </dl>
        <div className='returning-family-merge__profile-meta'>
            <span>{String(family?.school || '').toUpperCase() || 'No school'}</span>
            <span>Created {formatDate(family?.onboardDate || family?.createdAt)}</span>
        </div>
    </section>
)

const ReturningFamilyMergeModal = ({mergeRequest, onClose, onMerged}) => {
    const matches = Array.isArray(mergeRequest?.duplicateInfo?.matches)
        ? mergeRequest.duplicateInfo.matches : []
    const [sourceFamilyId, setSourceFamilyId] = React.useState('')
    const [preview, setPreview] = React.useState(null)
    const [decisions, setDecisions] = React.useState({})
    const [loading, setLoading] = React.useState(false)
    const [submitting, setSubmitting] = React.useState(false)
    const [error, setError] = React.useState(null)
    const [confirming, setConfirming] = React.useState(false)
    const [result, setResult] = React.useState(null)

    React.useEffect(() => {
        setSourceFamilyId(matches[0]?.familyId || '')
        setPreview(null)
        setDecisions({})
        setError(null)
        setConfirming(false)
        setResult(null)
    }, [mergeRequest])

    React.useEffect(() => {
        if (!mergeRequest?.currentFamilyId || !sourceFamilyId) return undefined
        let active = true
        const loadPreview = async () => {
            setLoading(true)
            setPreview(null)
            setError(null)
            setConfirming(false)
            try {
                const response = await axios.post(
                    `${baseUrl}/family-merges/preview`,
                    {survivorFamilyId: mergeRequest.currentFamilyId, sourceFamilyId},
                    getRequestHeaders()
                )
                if (!active) return
                setPreview(response.data)
                setDecisions((response.data?.source?.students || []).reduce((next, student) => ({
                    ...next, [student.studentId]: 'keep_historical',
                }), {}))
            } catch (requestError) {
                if (!active) return
                setError({
                    code: requestError?.response?.data?.code || 'FAMILY_MERGE_PREVIEW_FAILED',
                    message: requestError?.response?.data?.message || 'We could not safely compare these family profiles.',
                    status: requestError?.response?.status,
                })
            } finally {
                if (active) setLoading(false)
            }
        }
        loadPreview()
        return () => { active = false }
    }, [mergeRequest?.currentFamilyId, sourceFamilyId])

    if (!mergeRequest) return null

    const sourceStudents = preview?.source?.students || []
    const currentStudents = preview?.survivor?.students || []
    const sumCounts = (keys) => keys.reduce(
        (total, key) => total + (Number(preview?.referenceCounts?.[key]) || 0), 0
    )
    const updatedTimelineCount = sumCounts(['notes', 'statusHistory', 'teacherHistory', 'inquiryActivity'])
    const retainedRecordCount = sumCounts([
        'calendarLessons', 'cancellations', 'invoices', 'policies',
        'invitations', 'waitlistEntries', 'onboardingCards',
    ])
    const close = () => { if (!submitting) onClose() }

    const submitMerge = async () => {
        if (!preview || submitting) return
        setSubmitting(true)
        setError(null)
        try {
            const studentDecisions = sourceStudents.map((student) => {
                const value = decisions[student.studentId] || 'keep_historical'
                return value.startsWith('merge_with:')
                    ? {sourceStudentId: student.studentId, action: 'merge_with', targetStudentId: value.replace('merge_with:', '')}
                    : {sourceStudentId: student.studentId, action: 'keep_historical'}
            })
            const response = await axios.post(
                `${baseUrl}/family-merges`,
                {
                    survivorFamilyId: mergeRequest.currentFamilyId,
                    sourceFamilyId,
                    confirmation: MERGE_CONFIRMATION,
                    studentDecisions,
                },
                getRequestHeaders()
            )
            setConfirming(false)
            setResult(response.data)
            if (onMerged) await onMerged(response.data)
        } catch (requestError) {
            setConfirming(false)
            setError({
                code: requestError?.response?.data?.code || 'FAMILY_MERGE_FAILED',
                message: requestError?.response?.data?.message || 'The merge was not completed. Both profiles were left unchanged.',
                status: requestError?.response?.status,
            })
        } finally {
            setSubmitting(false)
        }
    }

    return <div className='returning-family-merge__backdrop' role='presentation' onMouseDown={close}>
        <div className='returning-family-merge__modal' role='dialog' aria-modal='true'
            aria-labelledby='returning-family-merge-title' onMouseDown={(event) => event.stopPropagation()}>
            <header className='returning-family-merge__header'>
                <div className='returning-family-merge__title-icon'><MdOutlineMerge aria-hidden='true' /></div>
                <div>
                    <span className='returning-family-merge__eyebrow'>Returning-family review</span>
                    <h2 id='returning-family-merge-title'>Review & merge duplicate profiles</h2>
                    <p>Bring useful history forward without replacing this inquiry’s current information.</p>
                </div>
                <button type='button' className='returning-family-merge__close' onClick={close} aria-label='Close merge review'><MdClose /></button>
            </header>
            <div className='returning-family-merge__body'>
                {matches.length > 1 && <label className='returning-family-merge__match-selector'>
                    <span>Previous profile to review</span>
                    <select value={sourceFamilyId} onChange={(event) => setSourceFamilyId(event.target.value)}>
                        {matches.map((match) => <option key={match.familyId} value={match.familyId}>
                            {match.familyName} — {match.familyStatus || 'No status'} · {match.school || 'No school'}
                        </option>)}
                    </select>
                </label>}
                {loading && <div className='returning-family-merge__loading' aria-live='polite'><span />Comparing the two family profiles…</div>}
                {error && <div className='returning-family-merge__error' role='alert'>
                    <MdWarningAmber /><div>
                        <strong>{error.status === 409 ? 'Safety check stopped this merge' : 'Unable to review this merge'}</strong>
                        <p>{error.message}</p><code>{error.code}</code>
                    </div>
                </div>}
                {result && <div className='returning-family-merge__success' role='status'>
                    <MdCheckCircle /><div><h3>Profiles merged safely</h3>
                        <p>The current inquiry remains active. The previous profile is archived and redirects here.</p>
                        {result?.mergeId && <span>Audit ID: {result.mergeId}</span>}
                        <button type='button' onClick={close}>Done</button>
                    </div>
                </div>}
                {preview && !result && <>
                    <div className='returning-family-merge__profiles'>
                        <ProfileSummary family={preview.survivor} label='Keep as current profile' survivor />
                        <div className='returning-family-merge__profile-arrow'><MdArrowForward /></div>
                        <ProfileSummary family={preview.source} label='Archive previous profile' />
                    </div>
                    <section className='returning-family-merge__section'>
                        <div className='returning-family-merge__section-heading'><MdPeople /><div>
                            <h3>Review previous students</h3>
                            <p>Old rates are retained for history only and never replace current pricing.</p>
                        </div></div>
                        <div className='returning-family-merge__student-list'>
                            {sourceStudents.length === 0 && <p className='returning-family-merge__empty'>No students are recorded on the previous profile.</p>}
                            {sourceStudents.map((student) => <div className='returning-family-merge__student-row' key={student.studentId}>
                                <div><strong>{student.studentName || 'Unnamed student'}</strong>
                                    <span>{student.studentStatus || 'No status'} · Previous rate {displayRate(student)}</span>
                                </div>
                                <label><span className='sr-only'>How should {student.studentName || 'this student'} be handled?</span>
                                    <select
                                        aria-label={`How should ${student.studentName || 'this student'} be handled?`}
                                        value={decisions[student.studentId] || 'keep_historical'}
                                        onChange={(event) => {
                                            const nextValue = event.target.value
                                            setDecisions((current) => ({...current, [student.studentId]: nextValue}))
                                        }}>
                                        <option value='keep_historical'>Keep as a historical student</option>
                                        {currentStudents.map((currentStudent) => <option key={currentStudent.studentId}
                                            value={`merge_with:${currentStudent.studentId}`}>
                                            Same person as {currentStudent.studentName || 'current student'}
                                        </option>)}
                                    </select>
                                </label>
                            </div>)}
                        </div>
                    </section>
                    <section className='returning-family-merge__section returning-family-merge__history'>
                        <div className='returning-family-merge__section-heading'><MdHistory /><div>
                            <h3>What happens to the history?</h3><p>Nothing is deleted.</p>
                        </div></div>
                        <div className='returning-family-merge__history-grid'>
                            <div><strong>{updatedTimelineCount}</strong><span>notes and activity entries join the current timeline</span></div>
                            <div><strong>{retainedRecordCount}</strong><span>calendar, billing, agreement, and workflow records stay on the archived source</span></div>
                        </div>
                        <ul>
                            <li>The latest address, phone, and email shown on the current inquiry win.</li>
                            <li>The current onboarding card keeps its existing family ID.</li>
                            <li>The previous profile remains available as an audited archive and redirects to this family.</li>
                        </ul>
                    </section>
                </>}
            </div>
            {preview && !result && <footer className='returning-family-merge__footer'>
                <button type='button' className='returning-family-merge__cancel' onClick={close}>Cancel</button>
                <button type='button' className='returning-family-merge__continue'
                    onClick={() => setConfirming(true)} disabled={submitting}>Continue to confirmation</button>
            </footer>}
            {confirming && <div className='returning-family-merge__confirm-backdrop' role='presentation'>
                <div className='returning-family-merge__confirm' role='alertdialog' aria-modal='true'>
                    <div className='returning-family-merge__confirm-icon'><MdWarningAmber /></div>
                    <h3>Archive and merge this previous profile?</h3>
                    <p><strong>{preview?.source?.familyName}</strong> will become an archived profile.
                        <strong> {preview?.survivor?.familyName}</strong> remains the live family and keeps its current contact details and pricing.</p>
                    <div className='returning-family-merge__confirm-actions'>
                        <button type='button' onClick={() => setConfirming(false)} disabled={submitting}>Go back</button>
                        <button type='button' className='is-dangerous' onClick={submitMerge} disabled={submitting}>
                            {submitting ? 'Merging safely…' : 'Yes, archive & merge'}
                        </button>
                    </div>
                </div>
            </div>}
        </div>
    </div>
}

export default ReturningFamilyMergeModal
